import { useAction, useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { PageHeader, PageSection } from '@root/shared/ui-kit'
import { layoutHeightCss } from '@root/shared/ui-kit/utils/page-layout'

import { reatomOrders } from './atoms/reatom-orders'

import type { Order, OrderToken } from './atoms/reatom-orders'
import type { ClassNameProps } from '@root/shared/react'

/**
 * What is waiting on a price.
 *
 * Both panels used to call deluge endpoints nobody brought over, so the page
 * showed "something went wrong" without ever reaching the network. It reads
 * the account's own orders now: the tokens on the left as a way into the
 * list, the orders themselves on the right.
 */
type OrdersPageProps = {} & ClassNameProps

export const OrdersPage: React.FC<OrdersPageProps> = ({ className }) => {
  const { t } = useTranslation()

  // eslint-disable-next-line @reatom/unit-naming-rule
  const model = useMemo(() => reatomOrders(), [])

  const [orders] = useAtom(model.ordersState)
  const [tokens] = useAtom(model.tokensState)
  const [selectedToken] = useAtom(model.selectedTokenAtom)

  const selectToken = useAction(model.selectTokenAction)
  const cancel = useAction(model.cancelAction)
  const cancelAll = useAction(model.cancelAllAction)

  const anyWaiting = AsyncStateN.matchWaiting(orders, {
    Waiting: () => false,
    Error: () => false,
    Success: (rows) => rows.some((row) => row.status === 'active'),
  })

  return (
    <Root className={className}>
      <PageHeader>
        <div>
          <PageHeader.Title>{t('orders.page.title')}</PageHeader.Title>
          <PageHeader.Description>
            {t('orders.page.description')}
          </PageHeader.Description>
        </div>
        {anyWaiting ? (
          <Button size="s" variant="secondary" onClick={() => cancelAll()}>
            {t('orders.cancel-all')}
          </Button>
        ) : undefined}
      </PageHeader>

      <Content>
        <CurrenciesSection>
          <SectionHeader>
            <PageSection.Title>
              {t('orders.page.active-tokens-orders')}
            </PageSection.Title>
          </SectionHeader>

          {AsyncStateN.matchWaiting(tokens, {
            Waiting: () => <Muted>{t('common.loading')}</Muted>,
            Error: () => <Muted>{t('common.something-went-wrong')}</Muted>,
            Success: (rows) =>
              rows.length === 0 ? (
                <Muted>{t('orders.empty')}</Muted>
              ) : (
                <TokenList>
                  <TokenRow
                    data-selected={selectedToken === undefined}
                    onClick={() => selectToken(undefined)}
                  >
                    <TokenName>{t('common.all')}</TokenName>
                    <Count>
                      {rows.reduce((sum, row) => sum + row.activeOrders, 0)}
                    </Count>
                  </TokenRow>
                  {rows.map((row: OrderToken) => (
                    <TokenRow
                      key={row.tokenAddress}
                      data-selected={selectedToken === row.tokenAddress}
                      onClick={() => selectToken(row.tokenAddress)}
                    >
                      <TokenName>
                        {row.symbol ?? shorten(row.tokenAddress)}
                      </TokenName>
                      <Count>{row.activeOrders}</Count>
                    </TokenRow>
                  ))}
                </TokenList>
              ),
          })}
        </CurrenciesSection>

        <OrdersSection>
          <SectionHeader>
            <PageSection.Title>{t('orders.page.title')}</PageSection.Title>
          </SectionHeader>

          {AsyncStateN.matchWaiting(orders, {
            Waiting: () => <Muted>{t('common.loading')}</Muted>,
            Error: () => <Muted>{t('common.something-went-wrong')}</Muted>,
            Success: (rows) =>
              rows.length === 0 ? (
                <Muted>{t('orders.empty')}</Muted>
              ) : (
                <OrderList>
                  {rows.map((row: Order) => (
                    <OrderRow key={row.id}>
                      <Side data-side={row.side}>
                        {row.side === 'buy'
                          ? t('trading.buy')
                          : t('trading.sell')}
                      </Side>
                      <Trigger>
                        {row.triggerType === 'price'
                          ? t('orders.trigger.price')
                          : t('orders.trigger.market-cap')}{' '}
                        {row.direction === 'above' ? '≥' : '≤'} $
                        {row.triggerValue}
                      </Trigger>
                      <Amount>{row.amount}</Amount>
                      <Status data-status={row.status}>
                        {row.error ?? row.status}
                      </Status>
                      {row.status === 'active' ? (
                        <Button
                          size="s"
                          variant="ghost"
                          onClick={() => cancel(row.id)}
                        >
                          {t('common.cancel')}
                        </Button>
                      ) : (
                        <span />
                      )}
                    </OrderRow>
                  ))}
                </OrderList>
              ),
          })}
        </OrdersSection>
      </Content>
    </Root>
  )
}

const shorten = (address: string) =>
  `${address.slice(0, 4)}…${address.slice(-4)}`

const Root = styled.div`
  ${layoutHeightCss}

  position: relative;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
`

const Content = styled.div`
  min-height: 0;
  overflow: hidden;

  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  padding: 10px;
  gap: 10px;
`

const CurrenciesSection = styled(PageSection)`
  min-height: 0;
  height: 100%;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
`

const OrdersSection = styled(PageSection)`
  min-height: 0;
  height: 100%;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
`

const SectionHeader = styled.div`
  padding-inline: 14px;
  padding-block: 14px;
`

const Muted = styled.div`
  padding: 24px 14px;
  color: var(--text-9);
  font-size: 12px;
`

const TokenList = styled.div`
  min-height: 0;
  overflow-y: auto;
`

const TokenRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  padding: 10px 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;

  &[data-selected='true'] {
    background: var(--background-2);
  }

  &:hover {
    background: var(--background-2);
  }
`

const TokenName = styled.span`
  color: var(--text-12);
  font-size: 12px;
  font-weight: 600;
`

const Count = styled.span`
  color: var(--text-9);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
`

const OrderList = styled.div`
  min-height: 0;
  overflow-y: auto;
`

const OrderRow = styled.div`
  display: grid;
  grid-template-columns: 60px minmax(0, 1fr) 120px 140px auto;
  gap: 10px;
  align-items: center;

  padding: 10px 14px;
  border-bottom: 1px solid var(--background-3);
  font-size: 12px;
`

const Side = styled.span`
  font-weight: 600;
  text-transform: uppercase;

  &[data-side='buy'] {
    color: var(--up-11);
  }

  &[data-side='sell'] {
    color: var(--down-11);
  }
`

const Trigger = styled.span`
  color: var(--text-11);
  font-variant-numeric: tabular-nums;
`

const Amount = styled.span`
  color: var(--text-11);
  font-variant-numeric: tabular-nums;
`

const Status = styled.span`
  color: var(--text-9);

  &[data-status='failed'] {
    color: var(--down-11);
  }
`
